import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';

/**
 * EventProposalsBanner
 *
 * Shared "Aidey spotted these in your emails" banner for email-detected event
 * proposals (Block 53/55). Self-contained: loads the workspace-scoped
 * /api/event-proposals endpoint, so it automatically shows only the active
 * workspace's proposals — the same component works in Family and Business views.
 *
 * Renders nothing when there are no pending proposals.
 *
 * Props:
 *   onChange?() — called after a proposal is accepted/resolved (host may refresh).
 *                 A global `aidey:tasksUpdated` event is also dispatched so any
 *                 calendar/task view listening for it re-fetches.
 */
export default function EventProposalsBanner({ onChange }) {
  const [proposals, setProposals] = useState([]);
  const [busy, setBusy]           = useState(null);   // proposal id mid-action
  const [reschedule, setReschedule] = useState({});   // { [id]: { suggestedStart, existingTitle } | 'noslot' }

  const load = useCallback(async () => {
    try {
      const res = await api('/api/event-proposals');
      if (res.ok) setProposals(await res.json());
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const drop = (id) => {
    setProposals(prev => prev.filter(p => p.id !== id));
    setReschedule(prev => { const n = { ...prev }; delete n[id]; return n; });
  };

  const notifyChanged = () => {
    if (typeof onChange === 'function') onChange();
    if (typeof window !== 'undefined') window.dispatchEvent(new Event('aidey:tasksUpdated'));
  };

  async function accept(p) {
    setBusy(p.id);
    try {
      const res = await api(`/api/event-proposals/${p.id}/accept`, { method: 'POST' });
      const data = res.ok ? await res.json() : null;
      if (data?.requiresResolution) return; // clash UI (from clash_result) drives resolution
      drop(p.id);
      notifyChanged();
    } catch { /* keep card on failure */ }
    finally { setBusy(null); }
  }

  async function resolve(p, action, confirm = false) {
    setBusy(p.id);
    try {
      const res = await api(`/api/event-proposals/${p.id}/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, confirm }),
      });
      const data = res.ok ? await res.json() : null;
      if (data?.preview) {
        setReschedule(prev => ({ ...prev, [p.id]: { suggestedStart: data.suggestedStart, existingTitle: data.existingTitle } }));
        return;
      }
      if (data?.noSlotFound) {
        setReschedule(prev => ({ ...prev, [p.id]: 'noslot' }));
        return;
      }
      drop(p.id);
      notifyChanged();
    } catch { /* keep card on failure */ }
    finally { setBusy(null); }
  }

  if (proposals.length === 0) return null;

  return (
    <div style={{ marginBottom: 20, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#92400e', marginBottom: 8 }}>
        Aidey spotted these in your emails
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {proposals.map(p => {
          const dateStr = new Date(p.event_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
          const clash = p.clash_result || {};
          const clashes = (clash.checked !== false && Array.isArray(clash.clashes)) ? clash.clashes : [];
          const hasClash = clashes.length > 0;
          const clashText = clashes.map(c =>
            c.source === 'booking' ? `booking "${c.title}"` : c.source === 'calendar' ? `"${c.title}" (${c.label || 'Calendar'})` : `"${c.title}"`
          ).join(', ');
          const isBusy = busy === p.id;
          const rs = reschedule[p.id];
          return (
            <div key={p.id} style={{ background: '#fff', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1a2e3b' }}>
                {p.title}{p.event_time ? ` · ${p.event_time}` : ''}
              </div>
              <div style={{ fontSize: 12, color: '#78350f', marginTop: 2 }}>
                {dateStr}{p.event_type && p.event_type !== 'other' ? ` · ${p.event_type.replace(/_/g, ' ')}` : ''}
              </div>
              {(p.source_sender || p.source_subject) && (
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                  From {p.source_sender || 'email'}{p.source_subject ? ` — "${p.source_subject}"` : ''}
                </div>
              )}

              {hasClash && (
                <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 6, fontWeight: 600 }}>
                  ⚠ Clashes with {clashText}
                </div>
              )}

              {rs === 'noslot' && (
                <div style={{ fontSize: 12, color: '#92400e', marginTop: 6 }}>
                  No free slot found nearby to move the existing item. Add anyway or skip?
                </div>
              )}
              {rs && rs !== 'noslot' && (
                <div style={{ fontSize: 12, color: '#166534', marginTop: 6 }}>
                  Move "{rs.existingTitle}" to {new Date(rs.suggestedStart).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}?
                </div>
              )}

              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                {!hasClash && (
                  <>
                    <button disabled={isBusy} onClick={() => accept(p)}
                      style={{ fontSize: 12, padding: '5px 12px', borderRadius: 6, border: '1px solid #059669', background: '#ecfdf5', color: '#065f46', cursor: isBusy ? 'not-allowed' : 'pointer', fontWeight: 600, opacity: isBusy ? 0.6 : 1 }}>
                      Add to calendar
                    </button>
                    <button disabled={isBusy} onClick={() => resolve(p, 'skip')}
                      style={{ fontSize: 12, padding: '5px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#9ca3af', cursor: isBusy ? 'not-allowed' : 'pointer' }}>
                      Ignore
                    </button>
                  </>
                )}
                {hasClash && !rs && (
                  <>
                    <button disabled={isBusy} onClick={() => resolve(p, 'move_existing')}
                      style={{ fontSize: 12, padding: '5px 12px', borderRadius: 6, border: '1px solid #2563eb', background: '#eff6ff', color: '#1d4ed8', cursor: isBusy ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
                      Move existing item
                    </button>
                    <button disabled={isBusy} onClick={() => resolve(p, 'add_anyway')}
                      style={{ fontSize: 12, padding: '5px 12px', borderRadius: 6, border: '1px solid #d97706', background: '#fffbeb', color: '#92400e', cursor: isBusy ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
                      Add anyway
                    </button>
                    <button disabled={isBusy} onClick={() => resolve(p, 'skip')}
                      style={{ fontSize: 12, padding: '5px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#9ca3af', cursor: isBusy ? 'not-allowed' : 'pointer' }}>
                      Skip this one
                    </button>
                  </>
                )}
                {hasClash && rs && rs !== 'noslot' && (
                  <>
                    <button disabled={isBusy} onClick={() => resolve(p, 'move_existing', true)}
                      style={{ fontSize: 12, padding: '5px 12px', borderRadius: 6, border: '1px solid #059669', background: '#ecfdf5', color: '#065f46', cursor: isBusy ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
                      Confirm move
                    </button>
                    <button disabled={isBusy} onClick={() => setReschedule(prev => { const n = { ...prev }; delete n[p.id]; return n; })}
                      style={{ fontSize: 12, padding: '5px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#6b7280', cursor: isBusy ? 'not-allowed' : 'pointer' }}>
                      Cancel
                    </button>
                  </>
                )}
                {hasClash && rs === 'noslot' && (
                  <>
                    <button disabled={isBusy} onClick={() => resolve(p, 'add_anyway')}
                      style={{ fontSize: 12, padding: '5px 12px', borderRadius: 6, border: '1px solid #d97706', background: '#fffbeb', color: '#92400e', cursor: isBusy ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
                      Add anyway
                    </button>
                    <button disabled={isBusy} onClick={() => resolve(p, 'skip')}
                      style={{ fontSize: 12, padding: '5px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#9ca3af', cursor: isBusy ? 'not-allowed' : 'pointer' }}>
                      Skip this one
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
